/**
 * Where a dim line lands. A dim's `offset` is normally null, because
 * placement is derived rather than authored: a dim is created before it is
 * bound to the geometry it measures, so neither the agent nor the tool knows
 * where the drawing's edge will be by the time the sheet is read.
 *
 * The rule is a drafter's. Every string stands clear of the drawing, on the
 * side it measures from, and strings stack: the finest against the drawing,
 * the overall dimension outermost, strings that do not overlap sharing a
 * line. A chain resolves as one group, so a chain is always one line, and an
 * offset authored on it (by dragging) carries the whole string.
 *
 * One rule covers all three directions. A dim line is `endpoint + growth *
 * offset` for the unit `growth` its direction defines (`dimLayout` draws it
 * that way), so a horizontal, vertical or raked string differ only in that
 * vector: the whole rule is projections onto it.
 */
import { invariant } from "ts-invariant";

import type { PageBox, PagePoint } from "./geometry";
import type { DimDirection } from "./shapes/dim";

export interface DimPlacement {
  id: string;
  direction: DimDirection;
  chain: string | null;
  /** Authored standoff in page units; null stacks the dim. */
  offset: number | null;
  /** Measured endpoints in page space (Y-down). */
  a: PagePoint;
  b: PagePoint;
}

/** Band spacing as a fraction of the drawing's long side, so a floor plan and
 *  a 1 1/2" detail both read right: 5% of a 40ft plan is the drafter's 2ft. */
const BAND_FRACTION = 0.05;
const EPSILON = 1e-6;

interface Span {
  min: number;
  max: number;
}

interface Group extends Span {
  members: DimPlacement[];
  /** The direction offsets grow along, shared by the whole string. */
  growth: PagePoint;
  /** Mean position along `growth`: which side of the drawing this belongs on. */
  cross: number;
  /** The line an authored offset asks for, along `growth`. */
  line: number | null;
}

/**
 * Resolve every dim's offset. Authored offsets pass through (and carry their
 * chain); the rest stack outside `drawing`, the boxes of every shape that is
 * drawing rather than annotation.
 */
export function resolveDimOffsets(
  dims: readonly DimPlacement[],
  drawing: readonly PageBox[],
): Map<string, number> {
  const box = drawingBox(drawing, dims);
  const step = BAND_FRACTION * Math.max(box.w, box.h);
  const bands = new Map<string, Span[][]>();
  const offsets = new Map<string, number>();
  for (const group of stackOrder(groupsOf(dims))) {
    const line = group.line ?? bandLine(group, box, step, bands);
    for (const dim of group.members) {
      offsets.set(dim.id, line - datum(dim, group.growth));
    }
  }
  return offsets;
}

function dot(p: PagePoint, q: PagePoint): number {
  return p.x * q.x + p.y * q.y;
}

/** The unit a dim's offset grows along: its line is `datum + growth * offset`. */
function growthOf(dim: DimPlacement): PagePoint {
  if (dim.direction === "x") return { x: 0, y: -1 };
  if (dim.direction === "y") return { x: -1, y: 0 };
  const span = { x: dim.b.x - dim.a.x, y: dim.b.y - dim.a.y };
  const length = Math.hypot(span.x, span.y) || 1;
  return { x: span.y / length, y: -span.x / length };
}

/** The endpoint a dim measures its offset from: the one furthest along the
 *  growth direction, which is where `dimLayout` starts the line. */
function datum(dim: DimPlacement, growth: PagePoint): number {
  return Math.max(dot(dim.a, growth), dot(dim.b, growth));
}

/** Everything the sheet is about: the drawn shapes plus what dims measure. */
function drawingBox(
  drawing: readonly PageBox[],
  dims: readonly DimPlacement[],
): PageBox {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const box of drawing) {
    xs.push(box.x, box.x + box.w);
    ys.push(box.y, box.y + box.h);
  }
  for (const dim of dims) {
    xs.push(dim.a.x, dim.b.x);
    ys.push(dim.a.y, dim.b.y);
  }
  if (!xs.length) return { x: 0, y: 0, w: 0, h: 0 };
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

function groupsOf(dims: readonly DimPlacement[]): Group[] {
  const members = new Map<string, DimPlacement[]>();
  for (const dim of dims) {
    // A chain is one string on one line; anything else stands alone.
    const key = `${dim.direction}:${dim.chain ?? dim.id}`;
    const existing = members.get(key);
    if (existing) existing.push(dim);
    else members.set(key, [dim]);
  }
  return [...members.values()].map(groupOf);
}

function groupOf(members: DimPlacement[]): Group {
  const first = members[0];
  invariant(first, "a dim group has members");
  const growth = growthOf(first);
  const along = { x: -growth.y, y: growth.x };
  const points = members.flatMap((dim) => [dim.a, dim.b]);
  const spans = points.map((p) => dot(p, along));
  const crosses = points.map((p) => dot(p, growth));
  return {
    members,
    growth,
    min: Math.min(...spans),
    max: Math.max(...spans),
    cross: crosses.reduce((sum, c) => sum + c, 0) / crosses.length,
    line: authoredLine(members, growth),
  };
}

function authoredLine(
  members: DimPlacement[],
  growth: PagePoint,
): number | null {
  for (const dim of members) {
    if (dim.offset === null) continue;
    return datum(dim, growth) + dim.offset;
  }
  return null;
}

/** Finest first, so the overall dimension is pushed outermost. A chain
 *  outranks the single dim spanning the same distance: the string reads
 *  inside its own total. */
function stackOrder(groups: Group[]): Group[] {
  return groups.sort(
    (a, b) =>
      a.max - a.min - (b.max - b.min) || b.members.length - a.members.length,
  );
}

/** The nearest free band on the side the group measures from, reserved. */
function bandLine(
  group: Group,
  box: PageBox,
  step: number,
  bands: Map<string, Span[][]>,
): number {
  const { growth } = group;
  const corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.w, y: box.y },
    { x: box.x + box.w, y: box.y + box.h },
    { x: box.x, y: box.y + box.h },
  ].map((corner) => dot(corner, growth));
  const center = dot({ x: box.x + box.w / 2, y: box.y + box.h / 2 }, growth);
  const side = group.cross >= center ? 1 : -1;
  const key = `${growth.x.toFixed(3)},${growth.y.toFixed(3)}:${side}`;
  const stack = bands.get(key) ?? [];
  bands.set(key, stack);
  let index = 0;
  while (stack[index]?.some((taken) => overlaps(group, taken))) index++;
  (stack[index] ??= []).push({ min: group.min, max: group.max });
  const edge = side > 0 ? Math.max(...corners) : Math.min(...corners);
  return edge + side * step * (index + 1);
}

/** Strings that only touch end to end share a line, as a drafter draws them. */
function overlaps(a: Span, b: Span): boolean {
  return a.min < b.max - EPSILON && b.min < a.max - EPSILON;
}
